import { Images } from '@/assets/images';
import { DocTypeEnum, GenderEnum, REGEX } from '@/constant';
import { cls } from '@/utils';

import { FormConfig } from '../define-document-write.type';

const initializeValue = {
	firstName: '',
	lastName: '',
	gender: '',
	birthDate: '',
	national: '',
	occupation: '',
	koreanProficiency: '',
	nameOfInvitingOrganization: '',
	reason: '',
	laborContractPeriodStart: '',
	laborContractPeriodEnd: '',
	workPlace: '',
	plan: '',
	other: '',
	commune: '',
	city: '',
	conscious: '',
	titleAndName: '',
	position: '', // '시‧군‧구',
};

type IValues = typeof initializeValue;

export const LETTER_OF_REASON_FOR_INVITING_FOREIGN_LANGUAGE_ASSISTANT_CONFIG: FormConfig<
	IValues,
	DocTypeEnum.AFR_07
> = {
	initializeValue,
	mapPropToValues({ applicantData, detailData }) {
		const { unitInfo, adminInfo } = detailData;

		return {
			...initializeValue,
			firstName: applicantData?.firstName || '',
			lastName: applicantData?.lastName || '',
			gender: applicantData?.gender || '',
			national: applicantData?.nationalName || '',
			birthDate: applicantData?.birthDate || '',
			nameOfInvitingOrganization: unitInfo?.name || '',
			city: unitInfo?.countyDistrict || '',
			conscious:
				(adminInfo?.isWorkerMasterRole
					? unitInfo?.name
					: adminInfo?.departmentName) || '',
			titleAndName:
				(adminInfo?.isWorkerMasterRole
					? unitInfo?.managerName
					: adminInfo?.unitManagerName) || '',
			commune: unitInfo?.cityProvince || '',
		};
	},
	title: '언어소통 도우미 해외초청 사유서',
	pages: [
		{
			background: {
				src: Images.formLetterOfReasonForInvitingForeignLanguageAssistantHighlight,
				className: 'template',
				dataSource:
					Images.formLetterOfReasonForInvitingForeignLanguageAssistant,
			},
			textAreas: [
				{
					name: 'firstName',
					maxHeight: 42,
					maxLength: 16,
					containerClassName:
						'absolute top-[280px] left-[153px] w-[114px] leading-[12px]',
					className: 'text-[14.67px] leading-none',
				},
				{
					name: 'lastName',
					maxHeight: 42,
					maxLength: 40,
					containerClassName:
						'absolute top-[280px] left-[270px] w-[270px] leading-[15px]',
					className: 'text-[14.67px] leading-none',
				},
				{
					name: 'national',
					maxHeight: 42,
					maxLength: 22,
					containerClassName: 'absolute top-[311px] left-[591px] w-[129px]',
					className: 'text-[14.67px] leading-none',
				},
				{
					name: 'occupation',
					maxHeight: 60,
					maxLength: 30,
					containerClassName: 'absolute top-[351px] left-[153px] w-[115px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					className: 'text-[14.67px] leading-none',
				},
				{
					name: 'koreanProficiency',
					maxHeight: 60,
					maxLength: 30,
					containerClassName: 'absolute top-[351px] left-[362px] w-[133px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					className: 'text-[14.67px] leading-none',
				},
				{
					name: 'nameOfInvitingOrganization',
					maxHeight: 60,
					maxLength: 30,
					containerClassName: 'absolute top-[351px] left-[591px] w-[124px]',
					className: 'text-[14.67px] leading-none',
				},
				{
					name: 'reason',
					maxHeight: 134,
					containerClassName: 'absolute top-[455px] left-[77px] w-[638px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'workPlace',
					maxHeight: 42,
					maxLength: 60,
					containerClassName: 'absolute top-[61%] left-[42.5%] w-[270px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'plan',
					maxHeight: 173,
					containerClassName: 'absolute top-[723px] left-[42.5%] w-[270px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'other',
					maxHeight: 42,
					containerClassName: 'absolute top-[80%] left-[42.5%] w-[270px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				},
				(values, index) => ({
					name: 'commune',
					maxHeight: 24,
					className: 'text-base text-right',
					containerClassName: cls(
						'absolute top-[1022px] left-[77px] w-[100px]',
						values?.commune && 'bg-white'
					),
					regex: REGEX.ALLOW_KR,
				}),
				(values, index) => ({
					name: 'city',
					maxHeight: 48,
					maxLength: 16,
					className: 'text-base text-center focus:bg-white bg-transparent',
					containerClassName: cls(
						'absolute top-[1011px] left-[200px] w-[140px] z-[1]',
						values?.city && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_SPECIAL_CHARS,
				}),
				(values, index) => ({
					name: 'position',
					maxHeight: 24,
					maxLength: 7,
					className: 'text-[18px] text-left',
					containerClassName: cls(
						'absolute top-[1023px] left-[249px] w-[72px]',
						values?.city && 'bg-white'
					),
					regex: REGEX.ALLOW_KR,
					readOnly: true,
				}),
				(values, index) => ({
					name: 'conscious',
					maxHeight: 24,
					className: 'text-base text-right',
					containerClassName: cls(
						'absolute top-[1023px] left-[321px] w-[87px]',
						values?.conscious && 'bg-white'
					),
					regex: REGEX.ALLOW_KR,
				}),
				(values, index) => ({
					name: 'titleAndName',
					maxHeight: 24,
					className: 'text-base',
					containerClassName: cls(
						'absolute top-[1024px] left-[544px] w-[200px]',
						values?.titleAndName && 'bg-white'
					),
				}),
			],
			checkboxes: [
				{
					name: 'gender',
					options: Object.values(GenderEnum),
					classNames: [
						'absolute top-[267px] left-[625px]',
						'absolute top-[286px] left-[625px]',
					],
					iconType: 'circle',
				},
			],
			datePickers: [
				{
					name: 'birthDate',
					className:
						'[&>.ant-picker-input>input]:!text-[14px] [&>.ant-picker-input>input]:!text-center w-[100%]',
					containerClassName: 'absolute left-[35%] top-[321px] w-[207px]',
					style: {
						width: '100%',
						textAlign: 'center',
					},
				},
			],
			rangePickers: [
				{
					name: ['laborContractPeriodStart', 'laborContractPeriodEnd'],
					containerClassName:
						'absolute top-[650px] left-[44%] w-[256px] h-[30px] [&>.ant-picker-range>.ant-picker-input>input]:!text-[14px] [&>.ant-picker-range>.ant-picker-input>input]:!text-center',
					style: {
						width: '100%',
						textAlign: 'center',
						fontSize: '14px',
					},
				},
			],
			signatures: [],
		},
		{
			background: {
				src: Images.formLetterOfReasonForInvitingForeignLanguageAssistantPage2,
				className: 'template',
				dataSource:
					Images.formLetterOfReasonForInvitingForeignLanguageAssistantPage2,
			},
		},
		{
			background: {
				src: Images.formLetterOfReasonForInvitingForeignLanguageAssistantPage3,
				className: 'template',
				dataSource:
					Images.formLetterOfReasonForInvitingForeignLanguageAssistantPage3,
			},
		},
	],
};
